import RfqStepBadge from '@/dealers/components/RfqStepBadge'
import StoreOrderStepBadge from '@/dealers/components/StoreOrderStepBadge'
import ButtonLink from '@/gf/components/ButtonLinkOld'
import Dropdown from '@/gf/components/Dropdown'
import Filters from '@/gf/components/Filters'
import FilterTabs from '@/gf/components/FilterTabs'
import Layout from '@/gf/components/Layout'
import Link from '@/gf/components/LinkOld'
import MachineDownIcon from '@/gf/components/MachineDownIcon'
import NeededByPill from '@/gf/components/NeededByPill'
import Page from '@/gf/components/Page'
import Pagination from '@/gf/components/Pagination'
import Spinner from '@/gf/components/Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import useFilters from '@/gf/hooks/useFilters'
import usePage from '@/gf/hooks/usePage'
import FilterContext from '@/gf/modules/Filter'
import Is from '@/gf/modules/filters/Is'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import RequestForQuoteM from '@/gf/modules/RequestForQuote'
import Time from '@/gf/modules/Time'
import UserM from '@/gf/modules/User'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import pluralize from 'pluralize'
import React from 'react'
import { JsonParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import { Filter, FilterField } from '../../types'
import Frame from '../components/Frame'
import useRequestForQuotesSearch from '../hooks/useRequestForQuotesSearch'
import useSession from '../hooks/useSession'

const stepOptions = [
  { id: 'inbound', display: 'Inbound' },
  { id: 'quoted', display: 'Quoted' },
  { id: 'po_received', display: 'PO Received' },
  { id: 'fulfilling', display: 'Fulfilling' },
  { id: 'fulfilled', display: 'Fulfilled' },
  { id: 'canceled', display: 'Canceled' },
]

const stateOptions = [
  { id: 'needs_approval', display: 'Needs Approval' },
  { id: 'not_quoted', display: 'Not Quoted' },
  { id: 'assigned', display: 'Assigned' },
  { id: 'quoted', display: 'Quoted' },
  { id: 'fulfilled', display: 'Fulfilled' },
  { id: 'closed', display: 'Closed' },
  { id: 'cancelled', display: 'Cancelled' },
  { id: 'rfq-open', display: 'RFQ Open' },
  { id: 'rfq-closed', display: 'RFQ Closed' },
]

const filterFields: FilterField[] = [
  {
    id: 'id',
    display: 'ID',
    filterTypeIds: ['equals'],
  },
  {
    id: 'full_name',
    display: 'Name',
    filterTypeIds: ['contains'],
  },
  {
    id: 'phone_number',
    display: 'Phone',
    filterTypeIds: ['contains'],
  },
  {
    id: 'organization_id',
    display: 'Organization',
    filterTypeIds: ['equals'],
  },
  {
    id: 'email_address',
    display: 'Email',
    filterTypeIds: ['contains'],
    adminOnly: true,
  },
  {
    id: 'parts_request',
    display: 'Request',
    filterTypeIds: ['contains'],
  },
  {
    id: 'rfq_assigned',
    display: 'Assigned',
    filterTypeIds: ['true'],
    adminOnly: true,
  },
  {
    id: 'rfq_created_by_email',
    display: 'RFQ Created By (Email)',
    filterTypeIds: ['contains'],
    adminOnly: true,
  },
  {
    id: 'rfq_created_by_name',
    display: 'RFQ Created By (Name)',
    filterTypeIds: ['contains'],
    adminOnly: true,
  },
  {
    id: 'quote_created_by_email',
    display: 'Order/Quote Created By (Email)',
    filterTypeIds: ['contains'],
    adminOnly: true,
  },
  {
    id: 'quote_created_by_name',
    display: 'Order/Quote Created By (Name)',
    filterTypeIds: ['contains'],
    adminOnly: true,
  },
  {
    id: 'step',
    display: 'State',
    filterTypeIds: ['is'],
    options: stepOptions,
  },
  {
    id: 'step',
    display: 'Step',
    filterTypeIds: ['is'],
    options: stateOptions,
  },
  {
    id: 'mpn',
    display: 'MPN',
    filterTypeIds: ['contains'],
  },
]

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'RFQs', href: '/rfqs' }],
}

const openRfqsFilters = [
  Is.build({
    fieldId: 'step',
    options: stateOptions,
    selectedOption: 'rfq-open',
  }),
]

const closedRfqsFilters = [
  Is.build({
    fieldId: 'step',
    options: stateOptions,
    selectedOption: 'rfq-closed',
  }),
]

const RequestsForQuotesPage = () => {
  const { user, store, featureFlags } = useSession()
  const [page, setPage] = usePage()

  const isAdmin = UserM.isAdmin(user)

  const opts = React.useMemo(
    () => ({
      filters: withDefault<Filter[], Filter[]>(JsonParam, isAdmin ? [] : []),
      tab: StringParam,
    }),
    [isAdmin]
  )

  const [query, updateQuery] = useQueryParams(opts)

  const [stagedFilters, filtersApi] = useFilters(query.filters)
  const fields = FilterContext.availableFilterFields(filterFields, isAdmin, featureFlags)

  const updateFilters = (newFilters: Filter[]) => {
    setPage(1)
    updateQuery({ filters: newFilters })
  }

  const [sortBy, setSortBy] = React.useState<{
    field: string
    order: 'DESC' | 'ASC'
  }>({ field: 'inserted_at', order: 'DESC' })

  const defaultFilters = FilterSet.toApiFilters(query.filters)

  const filters = isAdmin
    ? FormatFilters.andFilters(defaultFilters)
    : FormatFilters.andFilters([['equals', 'storeId', store.id], ...defaultFilters])

  const rfqsSearch = useRequestForQuotesSearch({ variables: { page, filters, sortBy } })
  const columns = 8 + (isAdmin ? 2 : 0)

  const selectedTab = query.tab || (isAdmin ? 'All' : 'Open')
  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page
        title="Requests for Quote"
        titleRightContent={
          isAdmin ? <ButtonLink href="/rfqs/create">Create RFQ</ButtonLink> : <></>
        }
      >
        <Layout>
          <Layout.Section type="table">
            <FilterTabs
              tabs={[
                {
                  name: 'Open',
                  filters: openRfqsFilters,
                },
                {
                  name: 'Closed',
                  filters: closedRfqsFilters,
                },
                {
                  name: 'All',
                  filters: [],
                },
              ]}
              currentTabName={selectedTab}
              onTabSelect={({ name, filters: tabFilters }) => {
                if (name === 'All' || name === 'Open' || name === 'Closed') {
                  updateQuery({ tab: name })
                  updateFilters(tabFilters)
                }
              }}
            />
            <Filters
              filters={query.filters}
              stagedFilters={stagedFilters}
              fields={fields}
              filtersApi={filtersApi}
              onApply={() => updateFilters(stagedFilters)}
            />
            <Table>
              <Thead>
                <tr>
                  <Th>RFQ ID</Th>
                  <Th>Status</Th>
                  <Th>Owner</Th>
                  <Th>Org.</Th>
                  {isAdmin && <Th>Quotes</Th>}
                  <Th>Name</Th>
                  {isAdmin && <Th>Email</Th>}
                  {!isAdmin && <Th>Phone Number</Th>}
                  <Th>Part Requests</Th>
                  <Th>Urgency</Th>
                  <Th>
                    <button
                      onClick={() =>
                        setSortBy((cur) => ({
                          field: 'inserted_at',
                          order: cur.order === 'ASC' ? 'DESC' : 'ASC',
                        }))
                      }
                      className="flex gap-1 uppercase tracking-wider"
                      type="button"
                    >
                      <span>Date/Time</span>
                      {sortBy.order === 'DESC' ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronUpIcon className="h-4 w-4" />
                      )}
                    </button>
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {rfqsSearch.data?.requestForQuotesSearch.requestForQuotes.map((rfq) => (
                  <Tr key={rfq.id}>
                    <Td>
                      <Link to={`/rfqs/${rfq.id}`}>{RequestForQuoteM.shortenId(rfq.id)}</Link>
                    </Td>
                    <Td>
                      <RfqStepBadge step={rfq.step} />
                    </Td>
                    <Td>
                      {rfq.requestForQuoteOwners.map(({ user }) => user.displayName).join(', ')}
                    </Td>
                    <Td>{rfq.user?.organization?.name}</Td>
                    {isAdmin && (
                      <td
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        className="whitespace-nowrap text-sm cursor-default text-gray-500 hover:text-gray-600"
                      >
                        <Dropdown
                          title={`${rfq.storeOrders.length} ${pluralize(
                            'quote',
                            rfq.storeOrders.length
                          )}`}
                          header="Quotes for RFQ"
                          stopEventPropagation
                        >
                          <ul className="flex flex-col space-y-2">
                            {rfq.storeOrders.map((so) => (
                              <li
                                className="flex flex-row space-x-2 justify-between items-center"
                                key={so.id}
                              >
                                <StoreOrderStepBadge step={so.step} />
                                <Link
                                  to={`/orders/${so.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {so.shortId}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </Dropdown>
                      </td>
                    )}
                    <Td>
                      {RequestForQuoteM.canDisplayCustomerContactInfo(rfq, user) && rfq.fullName}
                    </Td>
                    {isAdmin && <Td>{rfq.emailAddress}</Td>}
                    {!isAdmin && (
                      <Td>
                        {RequestForQuoteM.canDisplayCustomerContactInfo(rfq, user) &&
                          rfq.phoneNumber}
                      </Td>
                    )}
                    <Td className="p-0">
                      <div className="flex divide-x-1 divide-dotted">
                        {rfq.partRequests.map((pr) => (
                          <Dropdown title={pr.mpn} key={pr.mpn}>
                            <div className="grid grid-cols-3 auto-cols-auto w-80 whitespace-normal text-sm">
                              <span>MPN</span>
                              <span className="col-span-2">{pr.mpn}</span>
                              <span>Quantity</span>
                              <span className="col-span-2">{pr.quantity}</span>
                              <span>Desc.</span>
                              <span className="col-span-2">{pr.description}</span>
                            </div>
                          </Dropdown>
                        ))}
                      </div>
                    </Td>
                    <Td>
                      <div className="flex flex-row gap-x-1 items-center">
                        {rfq.machineDown && <MachineDownIcon tooltipLeft />}
                        <NeededByPill rfq={rfq} />
                      </div>
                    </Td>
                    <Td>{Time.toString(rfq.insertedAt)}</Td>
                  </Tr>
                ))}

                {!rfqsSearch.data && (
                  <Tr>
                    <Td colSpan={columns}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}

                {rfqsSearch.data?.requestForQuotesSearch.requestForQuotes.length === 0 && (
                  <Tr>
                    <Td colSpan={columns}>No RFQs found.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Layout.Section>

          <Layout.Section type="full">
            <Pagination
              pagination={rfqsSearch.data?.requestForQuotesSearch.pagination}
              page={page}
              updatePage={setPage}
            />
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  )
}

export default RequestsForQuotesPage
